import { useEffect, useMemo, useState } from 'react';
import { ManagementApi } from '../api/client';
import { ConfigCard, ConfigCheckbox, ConfigDraftJsonDrawer, ConfigEditorShell, ConfigField, ConfigHelperText, ConfigSegmentedControl, ConfigSelect, ConfigTextInput, ConfigToggle, defaultDirtyDraftGuardMessage, useConfigDraftEditor, useDirtyDraftGuard, type ConfigDraftSource, type ConfigSegmentOption, type ConfigSelectOption } from '../configEditor';
import { managementTokenStorageKey, readManagementToken } from '../auth/managementAuth';
import { Drawer } from '../components/Drawer';
import { RefreshActionButton } from '../components/RefreshActionButton';
import { PropertyTable, ResourceHeader, StatusStrip, Tabs } from '../components/ResourceDetail';
import { useI18n } from '../i18n';
import { formatSystemCopy, getSystemSettingsCopy, type SystemLocale } from './i18n';
import { buildSystemViewModel, emptySystemGlobalSettingsDraft, type ConsoleAssetEvidence, type SystemActionGuard, type SystemCutoverChecklistItem, type SystemGlobalSettingsDraft, type SystemSources, type SystemView, type SystemViewModel } from './model';

type ResourceTab = 'overview' | 'json';
type SystemDrawer = 'diagnostics' | 'sensitive-reveal' | 'action-preview' | 'settings-editor' | 'settings-draft-json' | null;

type SystemState = {
  loading: boolean;
  sources: SystemSources;
  model: SystemViewModel;
  errors: Record<string, string>;
};

type SystemGlobalSettingsNumberField = {
  [TKey in keyof SystemGlobalSettingsDraft]: SystemGlobalSettingsDraft[TKey] extends number ? TKey : never;
}[keyof SystemGlobalSettingsDraft];

export function SystemSettingsPage({ view, initialTab, initialDrawer = null }: { view: SystemView; initialTab?: string | null; initialDrawer?: SystemDrawer }) {
  const api = useMemo(() => new ManagementApi(), []);
  const { locale } = useI18n();
  const copy = getSystemSettingsCopy(locale);
  const [activeTab, setActiveTab] = useState<ResourceTab>(() => normalizeTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [drawer, setDrawer] = useState<SystemDrawer>(initialDrawer);
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [confirmationValue, setConfirmationValue] = useState('');
  const [state, setState] = useState<SystemState>(() => {
    const sources: SystemSources = { assetEvidence: collectAssetEvidence(), authClient: collectAuthClientEvidence() };
    return { loading: true, sources, model: buildSystemViewModel(sources, view, locale), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeTab(initialTab)), [initialTab]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const [systemOverview, status, personas, sessions, cron, relay, secrets, audit] = await Promise.all([
        capture('system overview', api.getSystemOverview(), errors),
        capture('status', api.getStatus(), errors),
        capture('personas', api.getPersonas(), errors),
        capture('sessions', api.getSessions({ limit: '25' }), errors),
        capture('cron', api.getCronJobs(), errors),
        capture('relay', api.getRelay(), errors),
        capture('secrets', api.getSecrets(), errors),
        capture('secret reveal audit', api.getAudit({ resource_kind: 'secret', action: 'secret.reveal', limit: '5' }), errors),
      ]);
      if (!mounted) return;
      const sources = { systemOverview, status, personas, sessions, cron, relay, secrets, audit, assetEvidence: collectAssetEvidence(), authClient: collectAuthClientEvidence(), errors };
      setState({ loading: false, sources, model: buildSystemViewModel(sources, view, locale), errors });
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, view, reloadToken]);

  useEffect(() => {
    setState(current => ({ ...current, model: buildSystemViewModel(current.sources, view, locale) }));
  }, [locale, view]);

  const { model } = state;
  const emptySettingsDraft = useMemo(() => emptySystemGlobalSettingsDraft(), []);
  const settingsDraftEditor = useConfigDraftEditor<SystemGlobalSettingsDraft>({
    current: model.globalSettingsDraft,
    published: model.globalSettingsDraft,
    empty: emptySettingsDraft,
    onSaveDraft: values => values,
  });
  const settingsDirtyGuard = useDirtyDraftGuard({ dirty: view === 'settings' && settingsDraftEditor.dirty, message: defaultDirtyDraftGuardMessage });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaveMessage, setSettingsSaveMessage] = useState('');
  const selectedAction = model.actionGuards.find(action => action.id === selectedActionId) ?? model.actionGuards[0];
  const openActionPreview = (action: SystemActionGuard) => {
    setSelectedActionId(action.id);
    setConfirmationValue('');
    setDrawer('action-preview');
  };
  const selectTab = (tab: string) => {
    const nextTab = normalizeTab(tab);
    if (nextTab === activeTab) return;
    settingsDirtyGuard.guardTabSwitch(nextTab, selectedTab => {
      settingsDraftEditor.discardChanges();
      setSettingsSaveMessage('');
      setActiveTab(normalizeTab(selectedTab));
    });
  };
  const saveSettingsDraft = async () => {
    setSettingsSaving(true);
    try {
      await settingsDraftEditor.saveDraft();
      setSettingsSaveMessage(copy.page.saveDraftLocalMessage);
    } finally {
      setSettingsSaving(false);
    }
  };
  const discardSettingsDraft = () => {
    settingsDraftEditor.discardChanges();
    setSettingsSaveMessage('');
  };
  const closeSettingsEditor = () => {
    if (settingsDraftEditor.dirty) {
      settingsDirtyGuard.guardDrawerClose(() => {
        settingsDraftEditor.discardChanges();
        setSettingsSaveMessage('');
        setDrawer(null);
      });
      return;
    }
    setDrawer(null);
  };

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow={copy.page.eyebrow}
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        actions={<SystemActions locale={locale} loading={state.loading} onOpenDiagnostics={() => setDrawer('diagnostics')} onRefresh={() => setReloadToken(value => value + 1)} />}
      />
      <StatusStrip items={model.status} />
      <SystemNotice loading={state.loading} notices={model.partialNotices} locale={locale} onOpenDiagnostics={() => setDrawer('diagnostics')} />
      <Tabs tabs={resourceTabs(locale)} activeId={activeTab} onSelect={selectTab} />

      {activeTab === 'overview'
        ? <SystemOverview model={model} view={view} locale={locale} onOpenDiagnostics={() => setDrawer('diagnostics')} onOpenReveal={() => setDrawer('sensitive-reveal')} onOpenActionPreview={openActionPreview} onOpenSettingsEditor={() => setDrawer('settings-editor')} />
        : <section className="panel"><pre className="json-preview">{model.rawJson}</pre></section>}

      <SettingsEditorDrawer
        dirty={settingsDraftEditor.dirty}
        draft={settingsDraftEditor.values}
        locale={locale}
        open={drawer === 'settings-editor'}
        saveMessage={settingsSaveMessage}
        saving={settingsSaving}
        source={settingsDraftEditor.source}
        onChangeDraft={settingsDraftEditor.setValues}
        onClose={closeSettingsEditor}
        onDiscard={discardSettingsDraft}
        onOpenDraftJson={() => setDrawer('settings-draft-json')}
        onSaveDraft={() => void saveSettingsDraft()}
      />
      <ConfigDraftJsonDrawer
        open={drawer === 'settings-draft-json'}
        onClose={() => setDrawer('settings-editor')}
        title={copy.page.globalSettingsDraftJsonTitle}
        closeLabel={copy.page.backToStructuredEdit}
        draft={settingsDraftEditor.values}
        published={settingsDraftEditor.publishedValue}
        diffTitle={copy.page.globalSettingsDraftDiffTitle}
        emptyDiffLabel={copy.page.globalSettingsEmptyDiff}
        redactionOptions={{ redactPromptContent: true }}
      />
      <DiagnosticsDrawer model={model} locale={locale} open={drawer === 'diagnostics'} onClose={() => setDrawer(null)} />
      <SensitiveRevealDrawer model={model} locale={locale} open={drawer === 'sensitive-reveal'} onClose={() => setDrawer(null)} />
      <ActionPreviewDrawer
        action={selectedAction}
        confirmationValue={confirmationValue}
        locale={locale}
        open={drawer === 'action-preview'}
        onChangeConfirmation={setConfirmationValue}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}

function SystemOverview({ model, view, locale, onOpenDiagnostics, onOpenReveal, onOpenActionPreview, onOpenSettingsEditor }: { model: SystemViewModel; view: SystemView; locale: SystemLocale; onOpenDiagnostics: () => void; onOpenReveal: () => void; onOpenActionPreview: (action: SystemActionGuard) => void; onOpenSettingsEditor: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  if (view === 'release-operations') {
    return (
      <div className="page-stack">
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.deploymentFreshness}</h2><p>{copy.page.releaseDeploymentDescription}</p></div></div>
          <PropertyTable rows={deploymentSummaryRows(model, locale)} />
        </section>
        <ActionGuardPanel actions={model.actionGuards} locale={locale} onOpenActionPreview={onOpenActionPreview} />
        <DiagnosticsEntrypointPanel locale={locale} onOpenDiagnostics={onOpenDiagnostics} />
      </div>
    );
  }

  if (view === 'settings') {
    return (
      <div className="page-stack">
        <SystemGlobalSettingsSummary model={model} locale={locale} onOpenSettingsEditor={onOpenSettingsEditor} />
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.authTokenMode}</h2><p>{copy.page.authTokenModeDescription}</p></div></div>
          <PropertyTable rows={model.authRows} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.deploymentFreshness}</h2><p>{copy.page.deploymentFreshnessDescription}</p></div></div>
          <PropertyTable rows={deploymentSummaryRows(model, locale)} />
        </section>
        <ActionEntrypointsPanel locale={locale} onOpenDiagnostics={onOpenDiagnostics} onOpenReveal={onOpenReveal} />
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.jsonRule}</h2><p>{copy.page.jsonRuleDescription}</p></div></div>
          <PropertyTable rows={model.jsonRuleRows.slice(0, 3)} />
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <div className="content-grid">
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.serviceBuild}</h2><p>{copy.page.serviceBuildDescription}</p></div></div>
          <PropertyTable rows={model.serviceRows} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.authTokenMode}</h2><p>{copy.page.authTokenModeDescription}</p></div></div>
          <PropertyTable rows={model.authRows} />
        </section>
      </div>

      <div className="content-grid">
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.deploymentFreshness}</h2><p>{copy.page.deploymentFreshnessDescription}</p></div></div>
          <PropertyTable rows={deploymentSummaryRows(model, locale)} />
        </section>
        <SystemGlobalSettingsSummary model={model} locale={locale} />
      </div>

      <ActionEntrypointsPanel locale={locale} onOpenDiagnostics={onOpenDiagnostics} onOpenReveal={onOpenReveal} />
    </div>
  );
}


function SystemGlobalSettingsSummary({ model, locale, onOpenSettingsEditor }: { model: SystemViewModel; locale: SystemLocale; onOpenSettingsEditor?: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>{copy.page.globalSettings}</h2><p>{copy.page.globalSettingsDescription}</p></div>
        {onOpenSettingsEditor ? <button className="btn btn-primary" type="button" onClick={onOpenSettingsEditor}>{copy.page.openStructuredEditor}</button> : null}
      </div>
      <PropertyTable rows={model.globalSettingsRows} />
      <PropertyTable rows={model.globalSettingsSourceRows} />
    </section>
  );
}

function SettingsEditorDrawer({ dirty, draft, locale, open, saveMessage, saving, source, onChangeDraft, onClose, onDiscard, onOpenDraftJson, onSaveDraft }: { dirty: boolean; draft: SystemGlobalSettingsDraft; locale: SystemLocale; open: boolean; saveMessage: string; saving: boolean; source: ConfigDraftSource; onChangeDraft: (draft: SystemGlobalSettingsDraft) => void; onClose: () => void; onDiscard: () => void; onOpenDraftJson: () => void; onSaveDraft: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <Drawer title={copy.page.globalSettingsEditorTitle} open={open} onClose={onClose} badge={dirty ? copy.page.unsavedDraft : copy.page.structuredDraft} closeLabel={copy.page.closeDrawer}>
      <SystemGlobalSettingsEditor
        dirty={dirty}
        draft={draft}
        locale={locale}
        saveMessage={saveMessage}
        saving={saving}
        source={source}
        onChangeDraft={onChangeDraft}
        onDiscard={onDiscard}
        onOpenDraftJson={onOpenDraftJson}
        onSaveDraft={onSaveDraft}
      />
    </Drawer>
  );
}

function SystemGlobalSettingsEditor({ dirty, draft, locale, saveMessage, saving, source, onChangeDraft, onDiscard, onOpenDraftJson, onSaveDraft }: { dirty: boolean; draft: SystemGlobalSettingsDraft; locale: SystemLocale; saveMessage: string; saving: boolean; source: ConfigDraftSource; onChangeDraft: (draft: SystemGlobalSettingsDraft) => void; onDiscard: () => void; onOpenDraftJson: () => void; onSaveDraft: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  const updateDraft = <TKey extends keyof SystemGlobalSettingsDraft>(field: TKey, value: SystemGlobalSettingsDraft[TKey]) => onChangeDraft({ ...draft, [field]: value });
  const updateNumber = (field: SystemGlobalSettingsNumberField, value: string) => updateDraft(field, parseNumberInput(value));
  const updateDisabledPlatforms = (value: string) => updateDraft('streamPreviewDisabledPlatforms', splitListInput(value));
  return (
    <ConfigEditorShell
      title={copy.page.globalSettingsEditorTitle}
      description={copy.page.globalSettingsEditorDescription}
      source={source}
      dirty={dirty}
      saving={saving}
      labels={{
        saveDraft: copy.page.saveDraft,
        discardChanges: copy.page.discardDraft,
        draftJson: copy.page.openDraftJsonDiff,
        saved: copy.page.noUnsavedDraft,
        unsaved: copy.page.unsavedDraft,
        sourceLabel: copy.page.prefillSource,
      }}
      onSaveDraft={onSaveDraft}
      onDiscardChanges={onDiscard}
      onOpenDraftJson={onOpenDraftJson}
    >
      <ConfigHelperText>{copy.page.globalSettingsStructuredBoundary}</ConfigHelperText>
      {saveMessage ? <div className="notice"><strong>{copy.page.draftSaved}</strong> {saveMessage}</div> : null}
      <div className="content-grid">
        <ConfigCard title={copy.page.generalSettings} description={copy.page.generalSettingsDescription}>
          <ConfigField id="system-language" label={copy.labels.language}>
            <ConfigSelect value={draft.language} onChange={value => updateDraft('language', value)} options={languageOptions(locale)} />
          </ConfigField>
          <ConfigField id="system-log-level" label={copy.labels.logLevel}>
            <ConfigSegmentedControl label={copy.labels.logLevel} value={draft.logLevel} onChange={value => updateDraft('logLevel', value)} options={logLevelOptions()} />
          </ConfigField>
          <ConfigField id="system-idle-timeout" label={copy.labels.idleTimeoutMins}>
            <ConfigTextInput inputMode="numeric" value={String(draft.idleTimeoutMins)} onChange={value => updateNumber('idleTimeoutMins', value)} />
          </ConfigField>
          <ConfigField label={copy.labels.cronSilent}>
            <ConfigCheckbox checked={draft.cronSilent} onChange={value => updateDraft('cronSilent', value)} label={copy.page.cronSilentCheckbox} />
          </ConfigField>
        </ConfigCard>
        <ConfigCard title={copy.page.displaySettings} description={copy.page.displaySettingsDescription}>
          <ConfigField id="system-display-mode" label={copy.labels.displayMode}>
            <ConfigSegmentedControl label={copy.labels.displayMode} value={draft.displayMode} onChange={value => updateDraft('displayMode', value)} options={displayModeOptions(locale)} />
          </ConfigField>
          <ConfigField id="system-thinking-max" label={copy.labels.displayThinkingMaxLen}>
            <ConfigTextInput inputMode="numeric" value={String(draft.displayThinkingMaxLen)} onChange={value => updateNumber('displayThinkingMaxLen', value)} />
          </ConfigField>
          <ConfigField id="system-tool-max" label={copy.labels.displayToolMaxLen}>
            <ConfigTextInput inputMode="numeric" value={String(draft.displayToolMaxLen)} onChange={value => updateNumber('displayToolMaxLen', value)} />
          </ConfigField>
          <ConfigField label={copy.labels.displayRouteDebug}>
            <ConfigToggle checked={draft.displayRouteDebug} onChange={value => updateDraft('displayRouteDebug', value)} label={copy.labels.displayRouteDebug} onLabel={copy.states.available} offLabel={copy.states.disabled} />
          </ConfigField>
        </ConfigCard>
        <ConfigCard title={copy.page.streamPreviewSettings} description={copy.page.streamPreviewSettingsDescription}>
          <ConfigField label={copy.labels.streamPreviewEnabled}>
            <ConfigToggle checked={draft.streamPreviewEnabled} onChange={value => updateDraft('streamPreviewEnabled', value)} label={copy.labels.streamPreviewEnabled} onLabel={copy.states.available} offLabel={copy.states.disabled} />
          </ConfigField>
          <ConfigField id="system-stream-interval" label={copy.labels.streamPreviewIntervalMs}>
            <ConfigTextInput inputMode="numeric" value={String(draft.streamPreviewIntervalMs)} onChange={value => updateNumber('streamPreviewIntervalMs', value)} />
          </ConfigField>
          <ConfigField id="system-stream-min-delta" label={copy.labels.streamPreviewMinDeltaChars}>
            <ConfigTextInput inputMode="numeric" value={String(draft.streamPreviewMinDeltaChars)} onChange={value => updateNumber('streamPreviewMinDeltaChars', value)} />
          </ConfigField>
          <ConfigField id="system-stream-max" label={copy.labels.streamPreviewMaxChars}>
            <ConfigTextInput inputMode="numeric" value={String(draft.streamPreviewMaxChars)} onChange={value => updateNumber('streamPreviewMaxChars', value)} />
          </ConfigField>
          <ConfigField id="system-stream-disabled-platforms" label={copy.labels.streamPreviewDisabledPlatforms} helperText={copy.page.streamPreviewDisabledPlatformsHelper}>
            <ConfigTextInput value={draft.streamPreviewDisabledPlatforms.join(', ')} onChange={updateDisabledPlatforms} />
          </ConfigField>
        </ConfigCard>
        <ConfigCard title={copy.page.limitsCleanupSettings} description={copy.page.limitsCleanupSettingsDescription}>
          <ConfigField label={copy.labels.rateLimitEnabled}>
            <ConfigToggle checked={draft.rateLimitEnabled} onChange={value => updateDraft('rateLimitEnabled', value)} label={copy.labels.rateLimitEnabled} onLabel={copy.states.available} offLabel={copy.states.disabled} />
          </ConfigField>
          <ConfigField id="system-rate-limit-max" label={copy.labels.rateLimitMaxMessages}>
            <ConfigTextInput inputMode="numeric" value={String(draft.rateLimitMaxMessages)} onChange={value => updateNumber('rateLimitMaxMessages', value)} />
          </ConfigField>
          <ConfigField id="system-rate-limit-window" label={copy.labels.rateLimitWindowSecs}>
            <ConfigTextInput inputMode="numeric" value={String(draft.rateLimitWindowSecs)} onChange={value => updateNumber('rateLimitWindowSecs', value)} />
          </ConfigField>
          <ConfigField label={copy.labels.sessionCleanupEnabled}>
            <ConfigCheckbox checked={draft.sessionCleanupEnabled} onChange={value => updateDraft('sessionCleanupEnabled', value)} label={copy.page.sessionCleanupCheckbox} />
          </ConfigField>
          <ConfigField id="system-cleanup-inactive-days" label={copy.labels.sessionCleanupInactiveDays}>
            <ConfigTextInput inputMode="numeric" value={String(draft.sessionCleanupInactiveDays)} onChange={value => updateNumber('sessionCleanupInactiveDays', value)} />
          </ConfigField>
          <ConfigField id="system-cleanup-keep-latest" label={copy.labels.sessionCleanupKeepLatest}>
            <ConfigTextInput inputMode="numeric" value={String(draft.sessionCleanupKeepLatest)} onChange={value => updateNumber('sessionCleanupKeepLatest', value)} />
          </ConfigField>
        </ConfigCard>
      </div>
    </ConfigEditorShell>
  );
}

function languageOptions(locale: SystemLocale): ConfigSelectOption[] {
  const copy = getSystemSettingsCopy(locale);
  return [
    { value: 'en', label: copy.page.languageEnglish },
    { value: 'zh', label: copy.page.languageChinese },
  ];
}

function logLevelOptions(): ConfigSegmentOption[] {
  return ['debug', 'info', 'warn', 'error'].map(value => ({ value, label: value }));
}

function displayModeOptions(locale: SystemLocale): ConfigSegmentOption[] {
  const copy = getSystemSettingsCopy(locale);
  return [
    { value: 'default', label: copy.page.displayModeDefault },
    { value: 'concise', label: copy.page.displayModeConcise },
    { value: 'quiet', label: copy.page.displayModeQuiet },
  ];
}

function parseNumberInput(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function splitListInput(value: string) {
  return value.split(',').map(item => item.trim()).filter(Boolean);
}

function deploymentSummaryRows(model: SystemViewModel, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  return model.deploymentRows.filter(([label]) => label !== copy.labels.backendAssetEvidence);
}

function ActionEntrypointsPanel({ locale, onOpenDiagnostics, onOpenReveal }: { locale: SystemLocale; onOpenDiagnostics: () => void; onOpenReveal: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{copy.page.actionCenter}</h2><p>{copy.page.actionCenterDescription}</p></div></div>
      <div className="action-card-grid">
        <div className="action-card">
          <h3>{copy.page.diagnosticsDetails}</h3>
          <p>{copy.page.diagnosticsDescription}</p>
          <button className="btn btn-compact" type="button" onClick={onOpenDiagnostics}>{copy.page.openDiagnostics}</button>
        </div>
        <div className="action-card">
          <h3>{copy.page.sensitiveReveal}</h3>
          <p>{copy.page.sensitiveRevealEntryDescription}</p>
          <button className="btn btn-compact" type="button" onClick={onOpenReveal}>{copy.page.openSensitiveReveal}</button>
        </div>
      </div>
    </section>
  );
}

function DiagnosticsEntrypointPanel({ locale, onOpenDiagnostics }: { locale: SystemLocale; onOpenDiagnostics: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>{copy.page.diagnosticsDetails}</h2><p>{copy.page.diagnosticsDescription}</p></div>
        <button className="btn btn-compact" type="button" onClick={onOpenDiagnostics}>{copy.page.openDiagnostics}</button>
      </div>
    </section>
  );
}

function SensitiveRevealDrawer({ model, locale, open, onClose }: { model: SystemViewModel; locale: SystemLocale; open: boolean; onClose: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <Drawer title={copy.page.sensitiveRevealDrawerTitle} open={open} onClose={onClose} badge={copy.states.contractGap} closeLabel={copy.page.closeDrawer}>
      <div className="page-stack">
        <p className="resource-description">{copy.page.sensitiveRevealDescription}</p>
        <div className="content-grid sensitive-reveal-grid">
          <div className="page-stack">
            <PropertyTable rows={model.sensitiveRevealRows} />
            <form className="reveal-guard-form" aria-label={copy.page.sensitiveRevealGuard}>
              <label className="field-label">
                {copy.labels.reenterToken}
                <input autoComplete="off" placeholder={copy.page.reentryPlaceholder} type="password" />
              </label>
              <button className="btn" type="button" disabled>{copy.page.revealDisabled}</button>
            </form>
          </div>
          <div className="page-stack">
            <h3 className="subsection-title">{copy.page.auditEvidence}</h3>
            <PropertyTable rows={model.sensitiveAuditRows} />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function DiagnosticsDrawer({ model, locale, open, onClose }: { model: SystemViewModel; locale: SystemLocale; open: boolean; onClose: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <Drawer title={copy.page.diagnosticsDrawerTitle} open={open} onClose={onClose} badge={copy.states.needsBackendDTO} closeLabel={copy.page.closeDrawer}>
      <div className="page-stack">
        {model.diagnosticNotices.length ? (
          <section className="panel">
            <div className="panel-header"><div><h2>{copy.page.diagnosticNotices}</h2><p>{copy.page.diagnosticNoticesDescription}</p></div></div>
            <ul className="evidence-list">
              {model.diagnosticNotices.map(notice => <li key={notice}>{notice}</li>)}
            </ul>
          </section>
        ) : null}
        <RequiredDTO model={model} locale={locale} />
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.diagnosticEvidence}</h2><p>{copy.page.diagnosticEvidenceDescription}</p></div></div>
          <div className="page-stack">
            <PropertyTable rows={model.healthRows} />
            <PropertyTable rows={model.runtimeRows} />
            <PropertyTable rows={model.schedulerRows} />
            <PropertyTable rows={model.platformRows.length ? model.platformRows : [[copy.labels.platforms, copy.page.platformFallback]]} />
            <PropertyTable rows={model.relaySecretRows} />
            <PropertyTable rows={model.legacyRows} />
          </div>
        </section>
        <CutoverChecklistPanel items={model.cutoverChecklist} locale={locale} />
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.rawDiagnosticJson}</h2><p>{copy.page.rawDiagnosticJsonDescription}</p></div></div>
          <pre className="json-preview">{model.diagnosticsJson}</pre>
        </section>
      </div>
    </Drawer>
  );
}

function ActionPreviewDrawer({ action, confirmationValue, locale, open, onChangeConfirmation, onClose }: { action?: SystemActionGuard; confirmationValue: string; locale: SystemLocale; open: boolean; onChangeConfirmation: (value: string) => void; onClose: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  if (!action) return null;
  const confirmed = confirmationValue === action.confirmationText;
  const executionUnavailable = !action.enabled || !action.previewSupported;
  return (
    <Drawer title={copy.page.releaseActionDrawerTitle} open={open} onClose={onClose} badge={executionUnavailable ? copy.states.unavailable : copy.states.previewRequired} closeLabel={copy.page.closeDrawer}>
      <div className="page-stack">
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.previewStep}</h2><p>{copy.page.previewStepDescription}</p></div></div>
          <PropertyTable rows={[
            [copy.page.action, action.label],
            [copy.page.state, action.state],
            [copy.page.guardReason, action.reason],
            [copy.labels.previewContract, action.previewSupported ? copy.states.available : copy.states.unavailable],
          ]} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{copy.page.typedConfirmation}</h2><p>{copy.page.typedConfirmationDescription}</p></div></div>
          <form className="page-stack" aria-label={copy.page.typedConfirmation}>
            <label className="field-label">
              {formatSystemCopy(copy.page.confirmationPhrase, { phrase: action.confirmationText })}
              <input autoComplete="off" value={confirmationValue} onChange={event => onChangeConfirmation(event.target.value)} />
            </label>
            <button className="btn btn-primary" type="button" disabled={executionUnavailable || !confirmed}>{executionUnavailable ? copy.page.operationUnavailable : copy.page.confirmOperation}</button>
          </form>
        </section>
      </div>
    </Drawer>
  );
}

function CutoverChecklistPanel({ items, locale }: { items: SystemCutoverChecklistItem[]; locale: SystemLocale }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{copy.page.cutoverChecklist}</h2><p>{copy.page.cutoverChecklistDescription}</p></div></div>
      <div className="table-scroll">
        <table className="data-table system-table">
          <thead><tr><th>{copy.page.check}</th><th>{copy.page.state}</th><th>{copy.page.evidence}</th></tr></thead>
          <tbody>{items.map(item => (
            <tr key={item.id}>
              <td data-label={copy.page.check}><strong>{item.label}</strong></td>
              <td data-label={copy.page.state}>{item.status}</td>
              <td data-label={copy.page.evidence}>{item.evidence}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

function ActionGuardPanel({ actions, locale, onOpenActionPreview }: { actions: SystemActionGuard[]; locale: SystemLocale; onOpenActionPreview: (action: SystemActionGuard) => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{copy.page.actionGuards}</h2><p>{copy.page.actionGuardsDescription}</p></div></div>
      <table className="data-table">
        <thead><tr><th>{copy.page.action}</th><th>{copy.page.state}</th><th>{copy.page.guardReason}</th><th>{copy.page.control}</th></tr></thead>
        <tbody>{actions.map(action => (
          <tr key={action.id}>
            <td><strong>{action.label}</strong></td>
            <td>{action.state}</td>
            <td>{action.reason}</td>
            <td><button className="btn btn-compact" type="button" onClick={() => onOpenActionPreview(action)}>{action.previewSupported ? copy.page.openPreview : copy.page.previewUnavailable}</button></td>
          </tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function RequiredDTO({ model, locale }: { model: SystemViewModel; locale: SystemLocale }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{copy.page.backendDTO}</h2><p>{copy.page.backendDTODescription}</p></div></div>
      <ul className="evidence-list">
        {model.requiredBackendDTO.map(item => <li key={item}>{item}</li>)}
      </ul>
    </section>
  );
}

function SystemActions({ locale, loading, onOpenDiagnostics, onRefresh }: { locale: SystemLocale; loading: boolean; onOpenDiagnostics: () => void; onRefresh: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  return (
    <div className="action-row">
      <button className="btn btn-primary" type="button" onClick={onOpenDiagnostics}>{copy.page.openDiagnostics}</button>
      <a className="btn btn-subtle" href="#/system">{copy.nav.settings}</a>
      <a className="btn btn-subtle" href="#/system/status">{copy.nav.status}</a>
      <a className="btn btn-subtle" href="#/system/release-operations">{copy.nav.releaseOps}</a>
      <RefreshActionButton loading={loading} onClick={onRefresh} />
    </div>
  );
}

function SystemNotice({ loading, notices, locale, onOpenDiagnostics }: { loading: boolean; notices: string[]; locale: SystemLocale; onOpenDiagnostics: () => void }) {
  const copy = getSystemSettingsCopy(locale);
  if (loading) return <div className="notice"><strong>{copy.page.loading}</strong></div>;
  if (!notices.length) return null;
  return (
    <div className="notice notice-warning">
      <strong>{copy.page.partialTitle}</strong>
      <span>{copy.page.partialSummary}</span>
      <button className="btn btn-compact" type="button" onClick={onOpenDiagnostics}>{copy.page.openDiagnostics}</button>
    </div>
  );
}

async function capture(label: string, promise: Promise<unknown>, errors: Record<string, string>) {
  try {
    return await promise;
  } catch (error) {
    errors[label] = error instanceof Error ? error.message : String(error);
    return undefined;
  }
}

function collectAssetEvidence(): ConsoleAssetEvidence {
  if (typeof window === 'undefined' || typeof document === 'undefined') return {};
  const runtimeWindow = window as Window & {
    __CC_CONNECT_ASSET_COMMIT__?: string;
    __CC_CONNECT_EXPECTED_COMMIT__?: string;
  };
  const script = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
    .find(item => item.src.includes('/console-v2/assets/'));
  const assetPath = script ? new URL(script.src, window.location.href).pathname : undefined;
  return {
    assetPath,
    assetCommit: runtimeWindow.__CC_CONNECT_ASSET_COMMIT__,
    expectedCommit: runtimeWindow.__CC_CONNECT_EXPECTED_COMMIT__,
  };
}

function collectAuthClientEvidence() {
  return {
    persistedToken: readManagementToken() !== null,
    storageKey: managementTokenStorageKey,
    logoutAvailable: true,
    handlesAuthErrors: true,
  };
}

function normalizeTab(value: string | null | undefined): ResourceTab {
  return value === 'json' ? 'json' : 'overview';
}

function resourceTabs(locale: SystemLocale): Array<{ id: ResourceTab; label: string }> {
  const copy = getSystemSettingsCopy(locale);
  return [
    { id: 'overview', label: copy.tabs.overview },
    { id: 'json', label: copy.tabs.json },
  ];
}
